import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import type { InstantiatedModel } from "../../systems/AssetManager";
import type { NpcStrideClips } from "./GroundedNpcGait";
import { NpcPoseAnimator, type NpcStatePose } from "./NpcPoseAnimator";

export interface GroundedNpcOptions {
  /** Actual authored support plane; the NPC's logical position stays intact. */
  floorY: number;
  shadowGenerator: ShadowGenerator;
  modelPath?: string;
  clips?: NpcStrideClips;
}

/** Owns presentation mounts, fallback support, loaded shadows and clip playback.
 * The base NPC remains the sole owner of model acquisition and model disposal. */
export class GroundedNpcPresentation {
  private readonly fallbackMount: TransformNode;
  private readonly fallbackMeshes: AbstractMesh[];
  private modelMount: TransformNode | null = null;
  private casters: AbstractMesh[] = [];
  private gait: NpcPoseAnimator | null = null;
  private previousX: number | null = null;
  private disposed = false;

  constructor(private readonly root: Mesh, parts: readonly AbstractMesh[],
    private readonly scale: number, private readonly options: GroundedNpcOptions) {
    this.fallbackMeshes = [root, ...parts];
    const minimum = this.minimumY(this.fallbackMeshes);
    const offset = options.floorY - minimum;
    // Bake the procedural capsule once; its transform remains the gameplay
    // target. The optional source model is never baked or edited here.
    root.bakeTransformIntoVertices(Matrix.Translation(0, offset, 0));
    this.fallbackMount = new TransformNode(root.name + "_fallbackMount", root.getScene());
    this.fallbackMount.parent = root; this.fallbackMount.position.y = offset;
    for (const part of parts) part.parent = this.fallbackMount;
    this.registerCasters(this.fallbackMeshes);
  }

  private minimumY(meshes: readonly AbstractMesh[]): number {
    return Math.min(...meshes.filter(m => m.getTotalVertices() > 0).map(m => {
      m.computeWorldMatrix(true); return m.getBoundingInfo().boundingBox.minimumWorld.y;
    }));
  }

  adopt(model: InstantiatedModel): void {
    if (this.disposed) return;
    // Source roots still carry the original glTF TRS. Measure them before
    // placing a separate scale/facing/support mount under the logical NPC.
    const minimum = this.minimumY(model.meshes);
    this.clearCasters();
    this.modelMount = new TransformNode(this.root.name + "_artMount", this.root.getScene());
    this.modelMount.parent = this.root;
    this.modelMount.scaling.setAll(this.scale);
    this.modelMount.rotationQuaternion = Quaternion.RotationAxis(Vector3.Up(), Math.PI / 2);
    this.modelMount.position.y = this.options.floorY - this.root.position.y - minimum * this.scale;
    for (const imported of model.rootNodes) imported.parent = this.modelMount;
    for (const part of this.fallbackMeshes) part.isVisible = false;
    if (this.root.isEnabled()) this.registerCasters(model.meshes);
    if (this.options.clips) this.gait = NpcPoseAnimator.create(model.animationGroups, this.scale, this.options.clips);
    this.gait?.setSuspended(!this.root.isEnabled());
    this.previousX = null;
  }

  hide(): void {
    this.root.setEnabled(false);
    this.gait?.setSuspended(true);
    this.clearCasters();
  }

  setPose(pose: NpcStatePose | null): void { this.gait?.setPose(pose); }

  update(dt: number): void {
    if (this.disposed) return;
    const distance = this.previousX === null ? 0 : Math.abs(this.root.position.x - this.previousX);
    this.previousX = this.root.position.x;
    // A QA placement / saved restore is not an animal stride.
    const speed = dt > 0 && distance < 1 ? distance / dt : 0;
    if (this.root.isEnabled()) this.gait?.update(speed, dt);
  }

  private registerCasters(meshes: readonly AbstractMesh[]): void {
    for (const mesh of meshes) if (mesh.getTotalVertices() > 0) {
      mesh.receiveShadows = true;
      this.options.shadowGenerator.addShadowCaster(mesh, false); this.casters.push(mesh);
    }
  }

  private clearCasters(): void {
    for (const mesh of this.casters) this.options.shadowGenerator.removeShadowCaster(mesh, false);
    this.casters = [];
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true; this.clearCasters(); this.gait?.dispose();
    // The base NPC disposes the imported model exactly once afterward.
    this.modelMount?.dispose(true); this.modelMount = null;
    this.fallbackMount.dispose();
  }
}
